// ============================================
// ROLC OneROLC - Admin Page
// Version: 3.6.0
// ============================================

import { CONFIG } from '../config/constants.js';
import {
    getToken, getName, getRole, getIsMasterAdmin, getMasterToken,
    setMasterToken, removeMasterToken, getSessionId
} from '../utils/storage.js';
import { $, $$, setContent, createElement } from '../utils/domHelpers.js';
import { capitalizeWords } from '../utils/formatters.js';
import { escapeHtml, stripHtml, showToast } from '../utils.js';
import {
    checkAdmin,
    adminSummary,
    adminChoiceDetail,
    getAdminSurveys,
    getAdminEvents,
    saveSurvey,
    deleteSurvey,
    saveEvent,
    deleteEvent,
    masterLogin,
    auditLogView,
    auditLogToggleVisibility,
    surveyArchiveToggleVisibility,
    bulkImportMembers,
    resetPin,
    searchMembers,
    getSheetUrl,
    getAuditLogVisibility,
    getSurveyArchiveVisibility,
    syncNow
} from '../api/endpoints.js';
import { navigateToHome } from '../router/routes.js';
import { logout } from '../auth/authService.js';
import { Modal, showConfirm, showAlert } from '../components/Modal.js';

// ============================================
// Global State
// ============================================
let quillEditor = null;
let currentSurveys = [];
let currentSurveyName = null;
let currentQuestions = [];
let currentReportData = null;
let currentReportFilter = null;
let currentReportDateFrom = null;
let currentReportDateTo = null;
let currentReportDateMode = 'today';
let masterToken = getMasterToken() || null;
let auditLogVisibility = null;
let archiveVisibility = null;
let modalOpenTime = 0;
let contentClickListenerAdded = false;
let accordionClickListenerAdded = false;

// ============================================
// Render Admin Page
// ============================================
export async function renderAdminPage(params = {}) {
    const token = getToken();
    const name = getName();

    if (!token || !name) {
        navigateToHome();
        return;
    }

    // Check if user is admin
    const adminCheck = await checkAdmin(token);
    if (!adminCheck.isAdmin) {
        if (adminCheck.error) {
            setContent(`
                <div class="card">
                    <div class="error-box">
                        <div class="error-icon">⚠️</div>
                        <p class="error-text">${escapeHtml(adminCheck.error)}</p>
                        <button class="btn btn-primary" id="btnAdminLogin">Go to Login</button>
                    </div>
                </div>
            `);
            document.getElementById('btnAdminLogin')?.addEventListener('click', () => {
                logout(false);
                window.location.replace('/login.html');
            });
        } else {
            setContent(`
                <div class="card">
                    <div class="error-box">
                        <div class="error-icon">🚫</div>
                        <p class="error-text">Admin Access Only</p>
                        <button class="btn btn-secondary" id="btnBackHome">Back to Home</button>
                    </div>
                </div>
            `);
            document.getElementById('btnBackHome')?.addEventListener('click', navigateToHome);
        }
        return;
    }

    // Load admin dashboard
    await loadAdminDashboard(token, adminCheck);
}

// ============================================
// Load Admin Dashboard
// ============================================
async function loadAdminDashboard(token, adminCheck, preloadedSurveys = null) {
    renderDashboardSkeleton();
    showToast('Loading dashboard...', 'info');

    try {
        // Get visibility states if master admin
        if (adminCheck.isMasterAdmin && masterToken) {
            try {
                const [auditVis, archiveVis] = await Promise.all([
                    getAuditLogVisibility().catch(() => ({ visibility: 'HIDDEN' })),
                    getSurveyArchiveVisibility().catch(() => ({ visibility: 'HIDDEN' }))
                ]);
                auditLogVisibility = auditVis.visibility || 'VERY_HIDDEN';
                archiveVisibility = archiveVis.visibility || 'VERY_HIDDEN';
            } catch (e) {
                auditLogVisibility = 'VERY_HIDDEN';
                archiveVisibility = 'VERY_HIDDEN';
            }
        }

        let surveysRes;
        if (preloadedSurveys) {
            surveysRes = { surveys: preloadedSurveys };
        } else {
            surveysRes = await getAdminSurveys(token);
        }

        const [summaryData, sheetUrlRes] = await Promise.all([
            adminSummary(token, 'today'),
            getSheetUrl(token).catch(() => ({ url: null }))
        ]);

        currentSurveys = surveysRes.surveys || [];
        const totalSurveys = currentSurveys.length;
        const openSurveys = currentSurveys.filter(s => s.open).length;
        const totalSubmissions = summaryData.total || 0;

        renderDashboardContent(
            adminCheck.fullName,
            totalSurveys,
            totalSubmissions,
            openSurveys,
            summaryData,
            adminCheck.isMasterAdmin || false,
            sheetUrlRes.url || null,
            currentSurveys,
            token
        );
    } catch (err) {
        const msg = err.message || String(err);
        if (msg.includes('Invalid token') || msg.includes('Session expired') || msg.includes('Token expired')) {
            showToast('Session expired. Please log in again.', 'error');
            logout(false);
            window.location.replace('/login.html');
            return;
        }
        showToast('Failed to load dashboard: ' + msg, 'error');
        setContent(`
            <div class="card">
                <div class="error-box">
                    <div class="error-icon">⚠️</div>
                    <p class="error-text">Unable to load control panel.<br>${escapeHtml(msg)}</p>
                    <button class="btn btn-primary" id="btnRetryAdmin">Retry</button>
                </div>
            </div>
        `);
        document.getElementById('btnRetryAdmin')?.addEventListener('click', () => renderAdminPage());
    }
}

// ============================================
// Render Dashboard Skeleton
// ============================================
function renderDashboardSkeleton() {
    const shimmer = 'background:linear-gradient(90deg,#e5e7eb 25%,#f3f4f6 50%,#e5e7eb 75%);background-size:200% 100%;animation:shimmer 1.5s infinite;';

    setContent(`
        <style>
            @keyframes shimmer {
                0% { background-position:200% 0 }
                100% { background-position:-200% 0 }
            }
            .skel { ${shimmer} border-radius:8px }
        </style>
        <div class="card">
            <div class="welcome-box" style="text-align:center;margin-bottom:20px;">
                <div class="welcome-label">Welcome,</div>
                <div class="skel" style="width:150px;height:28px;margin:8px auto;"></div>
            </div>
            <div class="stats-bar">
                <div class="stat-card"><div class="skel" style="width:40px;height:36px;margin:0 auto 4px;"></div><div class="stat-label">Surveys</div></div>
                <div class="stat-card"><div class="skel" style="width:40px;height:36px;margin:0 auto 4px;"></div><div class="stat-label">Open</div></div>
                <div class="stat-card"><div class="skel" style="width:40px;height:36px;margin:0 auto 4px;"></div><div class="stat-label">Submissions</div></div>
            </div>
            <div class="action-bar" style="margin-top:16px;">
                <button class="btn btn-success" id="btnCreateSurveySkeleton">➕ Create Survey</button>
                <button class="btn btn-success" id="btnBulkImportSkeleton" style="background:linear-gradient(135deg,#059669 0%,#10b981 100%);">📄 Bulk Import</button>
                <button class="btn btn-primary" id="btnViewAllReportsSkeleton">📊 View ALL Reports</button>
                <button class="btn btn-secondary" id="btnBackHomeSkeleton">🏠 Back to Home</button>
            </div>
        </div>
        <div class="survey-grid" style="margin-top:12px;">
            <div class="survey-admin-card" style="background:#f9fafb;">
                <div class="skel" style="width:60%;height:20px;margin-bottom:12px;"></div>
                <div class="skel" style="width:40%;height:16px;margin-bottom:8px;"></div>
                <div class="skel" style="width:80%;height:14px;"></div>
            </div>
            <div class="survey-admin-card" style="background:#f9fafb;">
                <div class="skel" style="width:50%;height:20px;margin-bottom:12px;"></div>
                <div class="skel" style="width:35%;height:16px;margin-bottom:8px;"></div>
                <div class="skel" style="width:70%;height:14px;"></div>
            </div>
        </div>
    `);

    document.getElementById('btnCreateSurveySkeleton')?.addEventListener('click', () => openSurveyModal());
    document.getElementById('btnBulkImportSkeleton')?.addEventListener('click', openBulkImportModal);
    document.getElementById('btnViewAllReportsSkeleton')?.addEventListener('click', () => openReportModal('today'));
    document.getElementById('btnBackHomeSkeleton')?.addEventListener('click', navigateToHome);
}

// ============================================
// Render Dashboard Content
// ============================================
function renderDashboardContent(adminName, totalSurveys, totalSubmissions, openSurveys, summaryData, isMasterAdmin, sheetUrl, surveys, token) {
    const statsHtml = `
        <div class="stats-bar">
            <div class="stat-card">
                <div class="stat-number">${totalSurveys}</div>
                <div class="stat-label">Surveys</div>
            </div>
            <div class="stat-card">
                <div class="stat-number">${openSurveys}</div>
                <div class="stat-label">Open</div>
            </div>
            <div class="stat-card">
                <div class="stat-number">${totalSubmissions}</div>
                <div class="stat-label">Submissions</div>
            </div>
        </div>
    `;

    const actionBar = `
        <div class="action-bar">
            <button class="btn btn-success" id="btnCreateSurvey">
                <span class="btn-icon">➕</span> Create Survey
            </button>
            <button class="btn btn-success" id="btnBulkImport" style="background:linear-gradient(135deg,#059669 0%,#10b981 100%);">
                <span class="btn-icon">📄</span> Bulk Import
            </button>
            <button class="btn btn-primary" id="btnViewAllReports">
                <span class="btn-icon">📊</span> View Reports
            </button>
            ${sheetUrl ? `<a href="${escapeHtml(sheetUrl)}" class="btn btn-secondary" style="text-decoration:none;" target="_blank"><span class="btn-icon">📄</span> Open Sheet</a>` : ''}
            <button class="btn btn-secondary" id="btnBackHome">
                <span class="btn-icon">🏠</span> Back to Home
            </button>
            <a href="#" id="btnForgotPin" style="margin-top:8px; display:inline-block; color:#4E8EF7; font-size:0.8rem; text-decoration:underline;"></a>
        </div>
    `;

    const pinResetHtml = `
        <div class="card" style="margin-top:12px; background:#f9fafb;">
            <p style="font-weight:600; font-size:0.95rem;">🔒 Reset Member PIN</p>
            <div style="position:relative; margin-top:8px;">
                <input type="text" id="resetPinName" placeholder="Type a name..." autocomplete="off" style="width:100%; padding:10px; font-size:1rem; border-radius:8px; border:2px solid #d1d5db;">
                <div id="resetPinSuggestions" style="position:absolute; top:100%; left:0; right:0; background:#fff; border:1px solid #e5e7eb; border-radius:8px; margin-top:4px; max-height:200px; overflow-y:auto; z-index:100; display:none; box-shadow:0 4px 12px rgba(0,0,0,0.15);"></div>
            </div>
            <button class="btn btn-secondary btn-sm" id="btnResetPin" style="margin-top:8px;">Reset PIN</button>
            <p id="resetPinMsg" style="font-size:0.8rem; color:#059669; margin-top:4px; display:none;"></p>
        </div>
    `;

    const advancedHtml = `
        <div class="card advanced-section" style="margin-top:12px; background:#f1f5f9;">
            <p style="font-weight:600; font-size:0.95rem;">⚙️ Advanced Settings</p>
            <div style="margin-top:8px; display:flex; gap:8px; flex-wrap: wrap;">
                ${!masterToken ?
                    `<button class="btn btn-secondary btn-sm" id="btnMasterLogin">🔑 Login</button>` :
                    `
                    <button class="btn btn-secondary btn-sm" id="btnAuditLog">📋 Audit Log</button>
                    <button class="btn btn-secondary btn-sm" id="btnSyncNow">🔄 Sync Now</button>
                    <button class="btn btn-secondary btn-sm" id="btnToggleAuditLog">${auditLogVisibility === 'VERY_HIDDEN' ? '🔒 AuditLog: HIDDEN' : '👁 AuditLog: VISIBLE'}</button>
                    <button class="btn btn-secondary btn-sm" id="btnToggleArchive">${archiveVisibility === 'VERY_HIDDEN' ? '🔒 Archives: HIDDEN' : '👁 Archives: VISIBLE'}</button>
                    <button class="btn btn-secondary btn-sm" id="btnMasterLogout">🔑 Logout</button>
                    `
                }
            </div>
            <div id="syncStatus" style="font-size:0.8rem; color:#6b7280; margin-top:4px; display:none;"></div>
        </div>
    `;

    let surveysHtml = '';
    if (surveys.length === 0) {
        surveysHtml = `
            <div class="card">
                <div class="error-box">
                    <div class="error-icon">📄</div>
                    <p class="error-text">No surveys yet.<br>Create your first survey above.</p>
                </div>
            </div>
        `;
    } else {
        const surveyCards = surveys.map(sv => {
            const accessClass = `badge-${sv.accessLevel}`;
            const openClass = sv.open ? 'badge-open' : 'badge-closed';
            const openText = sv.open ? 'Open' : 'Closed';
            const subCount = countSubmissionsForSurvey(sv.name, summaryData);
            const scheduleBadge = sv.autoSchedule ? `<span class="badge" style="background:linear-gradient(135deg,#7c3aed 0%,#a78bfa 100%);color:#fff;margin-left:4px;">🕐 Auto</span>` : '';

            return `
                <div class="survey-admin-card" data-name="${escapeHtml(sv.name)}">
                    <div class="survey-header-row" data-name="${escapeHtml(sv.name)}" style="cursor:pointer;display:flex;align-items:center;justify-content:space-between;padding:12px 16px;">
                        <div style="display:flex;align-items:center;gap:8px;flex-wrap:wrap;flex:1;">
                            <div class="survey-admin-name" style="font-weight:700;font-size:1.05rem;">${escapeHtml(sv.name)}</div>
                            <div class="survey-admin-meta" style="display:flex;gap:4px;flex-wrap:wrap;">
                                <span class="badge ${accessClass}">${sv.accessLevel}</span>
                                <span class="badge ${openClass}">${openText}</span>
                                ${scheduleBadge}
                            </div>
                        </div>
                        <span class="collapse-chevron" style="font-size:0.9rem;color:#6b7280;margin-left:8px;transition:transform 0.2s;">▶</span>
                    </div>
                    <div class="survey-expanded-content hidden" id="expanded-${escapeHtml(sv.name)}" style="padding:0 16px 12px;">
                        <div class="survey-admin-stats" style="padding:8px 0;border-top:1px solid #e2e8f0;color:#6b7280;font-size:0.9rem;">
                            ${sv.eventCount || 0} questions . ${subCount} submissions
                            ${sv.description ? `. ${escapeHtml(sv.description)}` : ''}
                        </div>
                        <div class="survey-admin-actions" style="display:flex;gap:6px;flex-wrap:wrap;padding:8px 0;border-top:1px solid #e2e8f0;">
                            <button class="btn btn-success btn-sm btn-add-question-bar" data-name="${escapeHtml(sv.name)}">➕ Add Question</button>
                            <button class="btn btn-primary btn-sm btn-edit-survey" data-name="${escapeHtml(sv.name)}">✏️ Edit</button>
                            <button class="btn btn-secondary btn-sm btn-preview-survey" data-name="${escapeHtml(sv.name)}">👁 Preview</button>
                            <button class="btn btn-secondary btn-sm btn-view-report" data-name="${escapeHtml(sv.name)}">📊 Report</button>
                            <button class="btn btn-warning btn-sm btn-duplicate-survey" data-name="${escapeHtml(sv.name)}">🔄 Duplicate</button>
                            <button class="btn btn-danger btn-sm btn-delete-survey" data-name="${escapeHtml(sv.name)}">🗑 Delete</button>
                        </div>
                        <div class="questions-panel" id="questions-${escapeHtml(sv.name)}" style="border-top:1px solid #e2e8f0;padding-top:8px;"></div>
                    </div>
                </div>
            `;
        }).join('');

        surveysHtml = `<div class="survey-grid">${surveyCards}</div>`;
    }

    setContent(`
        <div class="card">
            <div class="welcome-box" style="text-align:center;margin-bottom:20px;">
                <div class="welcome-label">Welcome,</div>
                <div class="welcome-name" style="font-size:1.2rem;">${escapeHtml(capitalizeWords(adminName))}</div>
            </div>
            ${statsHtml}
            ${actionBar}
            ${pinResetHtml}
            ${isMasterAdmin ? advancedHtml : ''}
        </div>
        ${surveysHtml}
    `);

    // Attach event listeners
    document.getElementById('btnCreateSurvey')?.addEventListener('click', () => openSurveyModal());
    document.getElementById('btnViewAllReports')?.addEventListener('click', () => openReportModal('today'));
    document.getElementById('btnBackHome')?.addEventListener('click', navigateToHome);
    document.getElementById('btnBulkImport')?.addEventListener('click', openBulkImportModal);
    document.getElementById('btnResetPin')?.addEventListener('click', handleResetPinClick);

    setupResetPinAutocomplete();

    // Advanced section listeners
    document.getElementById('btnMasterLogin')?.addEventListener('click', openMasterLoginModal);
    document.getElementById('btnAuditLog')?.addEventListener('click', openAuditLogModal);
    document.getElementById('btnSyncNow')?.addEventListener('click', handleSyncNow);
    document.getElementById('btnToggleAuditLog')?.addEventListener('click', handleAuditLogToggle);
    document.getElementById('btnToggleArchive')?.addEventListener('click', handleArchiveToggle);
    document.getElementById('btnMasterLogout')?.addEventListener('click', () => {
        masterToken = null;
        removeMasterToken();
        showToast('Master session cleared', 'info');
        renderAdminPage();
    });

    // Setup survey card actions
    setupSurveyCardActions();

    // Setup accordion
    setupAccordion();

    // Forgot PIN link
    document.getElementById('btnForgotPin')?.addEventListener('click', (e) => {
        e.preventDefault();
        // Focus on the reset PIN input
        document.getElementById('resetPinName')?.focus();
    });
}

// ============================================
// Setup Survey Card Actions (Event Delegation)
// ============================================
function setupSurveyCardActions() {
    if (contentClickListenerAdded) return;
    contentClickListenerAdded = true;

    document.addEventListener('click', (e) => {
        const btn = e.target.closest('button');
        if (!btn) return;
        const surveyName = btn.dataset.name;
        if (!surveyName) return;

        if (btn.classList.contains('btn-edit-survey')) {
            openSurveyModal(surveyName);
        } else if (btn.classList.contains('btn-preview-survey')) {
            previewSurvey(surveyName);
        } else if (btn.classList.contains('btn-view-report')) {
            openReportModal('today', surveyName);
        } else if (btn.classList.contains('btn-duplicate-survey')) {
            duplicateSurvey(surveyName);
        } else if (btn.classList.contains('btn-delete-survey')) {
            deleteSurveyPrompt(surveyName);
        } else if (btn.classList.contains('btn-add-question-bar')) {
            openQuestionModal(surveyName);
        }
    });
}

// ============================================
// Setup Accordion
// ============================================
function setupAccordion() {
    if (accordionClickListenerAdded) return;
    accordionClickListenerAdded = true;

    document.addEventListener('click', (e) => {
        const header = e.target.closest('.survey-header-row');
        if (!header) return;
        const card = header.closest('.survey-admin-card');
        if (!card) return;

        const expanded = card.querySelector('.survey-expanded-content');
        const chevron = header.querySelector('.collapse-chevron');
        if (!expanded) return;

        const isOpen = !expanded.classList.contains('hidden');

        // Close all other cards
        document.querySelectorAll('.survey-expanded-content').forEach(el => {
            if (el !== expanded) {
                el.classList.add('hidden');
            }
        });
        document.querySelectorAll('.collapse-chevron').forEach(el => {
            if (el !== chevron) el.textContent = '▶';
        });

        if (isOpen) {
            expanded.classList.add('hidden');
            if (chevron) chevron.textContent = '▶';
        } else {
            expanded.classList.remove('hidden');
            if (chevron) chevron.textContent = '▼';
            const panel = expanded.querySelector('.questions-panel');
            if (panel) loadQuestionsIntoPanel(panel, header.dataset.name);
        }
    });
}

// ============================================
// Load Questions Into Panel
// ============================================
async function loadQuestionsIntoPanel(panel, surveyName) {
    if (!panel) return;

    panel.innerHTML = `<div style="padding:12px;color:#6b7280;text-align:center;font-size:0.9rem;">Loading questions...</div>`;
    showToast('Loading questions...', 'info');

    const token = getToken();
    try {
        const data = await getAdminEvents(token, surveyName);
        currentQuestions = data.events || [];
        renderQuestionList(panel, surveyName, currentQuestions);
    } catch (err) {
        panel.innerHTML = `<p class="text-center text-danger" style="padding:12px;">Failed to load questions.</p>`;
    }
}

// ============================================
// Render Question List
// ============================================
function renderQuestionList(container, surveyName, questions) {
    if (questions.length === 0) {
        container.innerHTML = `
            <p style="color:var(--text-light);text-align:center;padding:12px;">No questions yet.</p>
        `;
        return;
    }

    const rows = questions.map((q, idx) => `
        <div class="question-row" style="display:flex;justify-content:space-between;align-items:center;padding:10px 12px;background:#f8fafc;border-radius:8px;margin-bottom:8px;font-size:0.95rem;">
            <span class="question-text" style="color:var(--text);font-weight:500;flex:1;padding-right:12px;white-space:pre-wrap;">${escapeHtml(stripHtml(q.name))} ${q.required !== false ? '<span style="color:#dc2626;font-weight:800;">*</span>' : '<span style="color:#94a3b8;font-size:0.75rem;">(Optional)</span>'}</span>
            <span class="question-type" style="font-size:0.75rem;color:var(--text-light);background:#e2e8f0;padding:2px 8px;border-radius:6px;white-space:nowrap;">${q.type}${q.maxSelect > 1 ? ' (' + q.maxSelect + ')' : ''}${q.type === 'date' && q.dateFormat ? ' (' + q.dateFormat + ')' : ''}${q.type === 'composite' && q.subQuestions && q.subQuestions.length > 0 ? ' (' + q.subQuestions.length + ' sub)' : ''}</span>
            <div class="question-actions" style="display:flex;gap:6px;margin-left:8px;">
                <button class="btn-ghost btn-edit-question" data-survey="${escapeHtml(surveyName)}" data-idx="${idx}" style="background:transparent;border:none;color:var(--primary-light);font-size:0.85rem;font-weight:600;cursor:pointer;padding:4px 8px;border-radius:6px;">✏️</button>
                <button class="btn-ghost text-danger btn-delete-question" data-survey="${escapeHtml(surveyName)}" data-idx="${idx}" style="background:transparent;border:none;color:var(--danger);font-size:0.85rem;font-weight:600;cursor:pointer;padding:4px 8px;border-radius:6px;">🗑</button>
            </div>
        </div>
    `).join('');

    container.innerHTML = `
        <div style="margin-bottom:12px;">${rows}</div>
    `;

    // Attach event listeners for question actions
    container.querySelectorAll('.btn-edit-question').forEach(btn => {
        btn.addEventListener('click', () => {
            const sName = btn.dataset.survey;
            const idx = parseInt(btn.dataset.idx, 10);
            openQuestionModal(sName, idx);
        });
    });

    container.querySelectorAll('.btn-delete-question').forEach(btn => {
        btn.addEventListener('click', () => {
            const sName = btn.dataset.survey;
            const idx = parseInt(btn.dataset.idx, 10);
            deleteQuestionPrompt(sName, idx);
        });
    });
}

// ============================================
// Survey Modal (Create/Edit)
// ============================================
function openSurveyModal(surveyName = null) {
    const isEdit = !!surveyName;
    const survey = isEdit ? currentSurveys.find(s => s.name === surveyName) : null;

    const modal = new Modal({
        title: isEdit ? 'Edit Survey' : 'Create New Survey',
        content: buildSurveyModalContent(isEdit, survey),
        confirmText: isEdit ? 'Save Changes' : 'Create Survey',
        cancelText: 'Cancel',
        onConfirm: () => handleSurveySave(isEdit, surveyName)
    });

    modal.open();

    // Setup toggle handlers after modal is open
    setTimeout(() => {
        setupSurveyModalHandlers(isEdit, survey);
    }, 100);
}

// ============================================
// Build Survey Modal Content
// ============================================
function buildSurveyModalContent(isEdit, survey) {
    return `
        <div class="form-group">
            <label>Survey Name</label>
            <input type="text" id="surveyNameInput" value="${isEdit ? escapeHtml(survey.name) : ''}" ${isEdit ? 'disabled' : ''} placeholder="e.g. Praise Drills">
            ${isEdit ? '<p class="form-hint">Survey name cannot be changed. Create a new survey instead.</p>' : ''}
        </div>
        <div class="form-group">
            <label>Description (optional)</label>
            <input type="text" id="surveyDescInput" value="${isEdit ? escapeHtml(survey.description || '') : ''}" placeholder="Short subtitle shown on home page">
        </div>
        <div class="form-group">
            <label>Access Level</label>
            <select id="surveyAccessInput">
                <option value="member" ${survey && survey.accessLevel === 'member' ? 'selected' : ''}>Member</option>
                <option value="leader" ${survey && survey.accessLevel === 'leader' ? 'selected' : ''}>Leader</option>
                <option value="admin" ${survey && survey.accessLevel === 'admin' ? 'selected' : ''}>Admin</option>
            </select>
        </div>
        <div class="toggle-row">
            <span class="toggle-label">Survey Open</span>
            <input type="checkbox" class="toggle-switch" id="surveyOpenInput" ${!survey || survey.open ? 'checked' : ''}>
        </div>
        <div class="toggle-row" style="margin-top:12px;">
            <span class="toggle-label">Auto Schedule</span>
            <input type="checkbox" class="toggle-switch" id="surveyAutoScheduleInput" ${survey && survey.autoSchedule ? 'checked' : ''}>
        </div>
        <div id="scheduleFields" style="display:${survey && survey.autoSchedule ? 'block' : 'none'};margin-top:12px;padding:12px;background:#f8fafc;border-radius:8px;">
            <div style="display:flex;gap:6px;margin-bottom:12px;">
                <button type="button" class="sched-type-btn" data-type="date" style="flex:1;padding:8px;border:1px solid #d1d5db;border-radius:8px;background:#fff;font-size:0.85rem;cursor:pointer;font-weight:600;">📅 Date Range</button>
                <button type="button" class="sched-type-btn" data-type="repeat" style="flex:1;padding:8px;border:1px solid #d1d5db;border-radius:8px;background:#fff;font-size:0.85rem;cursor:pointer;font-weight:600;">🔄 Recurring</button>
            </div>
            <div id="dateRangeFields" style="display:none;">
                <div class="form-group" style="margin-bottom:8px;">
                    <label style="font-size:0.85rem;">Start Date</label>
                    <input type="date" id="surveyStartDateInput" value="${isEdit ? escapeHtml(survey.startDate || '') : ''}">
                </div>
                <div class="form-group" style="margin-bottom:8px;">
                    <label style="font-size:0.85rem;">End Date</label>
                    <input type="date" id="surveyEndDateInput" value="${isEdit ? escapeHtml(survey.endDate || '') : ''}">
                </div>
            </div>
            <div class="form-group" style="margin-bottom:8px;">
                <label style="font-size:0.85rem;">Start Time <span style="font-weight:400;color:#9ca3af;">(e.g. 09:00)</span></label>
                <input type="time" id="surveyStartTimeInput" value="${isEdit ? escapeHtml(survey.startTime || '') : ''}">
            </div>
            <div class="form-group" style="margin-bottom:8px;">
                <label style="font-size:0.85rem;">End Time <span style="font-weight:400;color:#9ca3af;">(e.g. 17:00)</span></label>
                <input type="time" id="surveyEndTimeInput" value="${isEdit ? escapeHtml(survey.endTime || '') : ''}">
            </div>
            <div id="repeatFields" style="display:none;margin-bottom:0;">
                <div style="display:flex;gap:6px;margin:4px 0 8px;">
                    <button type="button" class="pattern-btn" data-pattern="Weekdays" style="flex:1;padding:6px 8px;border:1px solid #d1d5db;border-radius:6px;background:#fff;font-size:0.8rem;cursor:pointer;font-weight:500;">Weekdays</button>
                    <button type="button" class="pattern-btn" data-pattern="Everyday" style="flex:1;padding:6px 8px;border:1px solid #d1d5db;border-radius:6px;background:#fff;font-size:0.8rem;cursor:pointer;font-weight:500;">Everyday</button>
                    <button type="button" class="pattern-btn" data-pattern="Custom" style="flex:1;padding:6px 8px;border:1px solid #d1d5db;border-radius:6px;background:#fff;font-size:0.8rem;cursor:pointer;font-weight:500;">Custom</button>
                </div>
                <div id="repeatDays" style="display:flex;gap:4px;flex-wrap:wrap;">
                    <button type="button" class="day-btn" data-day="Sun" style="flex:1;min-width:32px;padding:6px 4px;border:1px solid #d1d5db;border-radius:6px;background:#fff;font-size:0.75rem;cursor:pointer;">Sun</button>
                    <button type="button" class="day-btn" data-day="Mon" style="flex:1;min-width:32px;padding:6px 4px;border:1px solid #d1d5db;border-radius:6px;background:#fff;font-size:0.75rem;cursor:pointer;">Mon</button>
                    <button type="button" class="day-btn" data-day="Tue" style="flex:1;min-width:32px;padding:6px 4px;border:1px solid #d1d5db;border-radius:6px;background:#fff;font-size:0.75rem;cursor:pointer;">Tue</button>
                    <button type="button" class="day-btn" data-day="Wed" style="flex:1;min-width:32px;padding:6px 4px;border:1px solid #d1d5db;border-radius:6px;background:#fff;font-size:0.75rem;cursor:pointer;">Wed</button>
                    <button type="button" class="day-btn" data-day="Thu" style="flex:1;min-width:32px;padding:6px 4px;border:1px solid #d1d5db;border-radius:6px;background:#fff;font-size:0.75rem;cursor:pointer;">Thu</button>
                    <button type="button" class="day-btn" data-day="Fri" style="flex:1;min-width:32px;padding:6px 4px;border:1px solid #d1d5db;border-radius:6px;background:#fff;font-size:0.75rem;cursor:pointer;">Fri</button>
                    <button type="button" class="day-btn" data-day="Sat" style="flex:1;min-width:32px;padding:6px 4px;border:1px solid #d1d5db;border-radius:6px;background:#fff;font-size:0.75rem;cursor:pointer;">Sat</button>
                </div>
                <input type="hidden" id="surveyRepeatInput" value="Everyday">
                <input type="hidden" id="surveyCustomDaysInput" value="">
            </div>
        </div>
    `;
}

// ============================================
// Setup Survey Modal Handlers
// ============================================
function setupSurveyModalHandlers(isEdit, survey) {
    // Auto-schedule toggle
    const autoToggle = document.getElementById('surveyAutoScheduleInput');
    if (autoToggle) {
        autoToggle.addEventListener('change', () => {
            const fields = document.getElementById('scheduleFields');
            if (fields) {
                fields.style.display = autoToggle.checked ? 'block' : 'none';
            }
        });
    }

    // Schedule type toggle
    const schedBtns = document.querySelectorAll('.sched-type-btn');
    schedBtns.forEach(btn => {
        btn.addEventListener('click', () => {
            const type = btn.dataset.type;
            schedBtns.forEach(b => {
                b.style.background = b === btn ? '#7c3aed' : '#fff';
                b.style.color = b === btn ? '#fff' : '#374151';
                b.style.borderColor = b === btn ? '#7c3aed' : '#d1d5db';
            });
            const dateFields = document.getElementById('dateRangeFields');
            const repeatFields = document.getElementById('repeatFields');
            if (type === 'date') {
                if (dateFields) dateFields.style.display = 'block';
                if (repeatFields) repeatFields.style.display = 'none';
            } else {
                if (dateFields) dateFields.style.display = 'none';
                if (repeatFields) repeatFields.style.display = 'block';
            }
        });
    });

    // Pattern buttons
    const patternBtns = document.querySelectorAll('.pattern-btn');
    const dayBtns = document.querySelectorAll('.day-btn');

    function setPatternBtn(pattern) {
        patternBtns.forEach(btn => {
            if (btn.dataset.pattern === pattern) {
                btn.style.background = '#7c3aed';
                btn.style.color = '#fff';
                btn.style.borderColor = '#7c3aed';
            } else {
                btn.style.background = '#fff';
                btn.style.color = '#374151';
                btn.style.borderColor = '#d1d5db';
            }
        });
        document.getElementById('surveyRepeatInput').value = pattern;
    }

    function setDayBtn(day, selected) {
        const btn = document.querySelector(`.day-btn[data-day="${day}"]`);
        if (!btn) return;
        btn.dataset.selected = selected ? '1' : '0';
        btn.style.background = selected ? '#7c3aed' : '#fff';
        btn.style.color = selected ? '#fff' : '#374151';
        btn.style.borderColor = selected ? '#7c3aed' : '#d1d5db';
    }

    function updateCustomDays() {
        const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
        const selected = days.filter(d => {
            const btn = document.querySelector(`.day-btn[data-day="${d}"]`);
            return btn && btn.dataset.selected === '1';
        });
        document.getElementById('surveyCustomDaysInput').value = selected.join(',');
    }

    patternBtns.forEach(btn => {
        btn.addEventListener('click', () => {
            const pattern = btn.dataset.pattern;
            if (pattern === 'Everyday') {
                ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].forEach(d => setDayBtn(d, true));
            } else if (pattern === 'Weekdays') {
                ['Sun', 'Sat'].forEach(d => setDayBtn(d, false));
                ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'].forEach(d => setDayBtn(d, true));
            }
            setPatternBtn(pattern);
            updateCustomDays();
        });
    });

    dayBtns.forEach(btn => {
        btn.addEventListener('click', () => {
            const isSelected = btn.dataset.selected === '1';
            setDayBtn(btn.dataset.day, !isSelected);
            // Detect pattern
            const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
            const selected = days.filter(d => {
                const b = document.querySelector(`.day-btn[data-day="${d}"]`);
                return b && b.dataset.selected === '1';
            });
            const weekdays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'];
            const isWeekdays = weekdays.every(d => selected.includes(d)) && !selected.includes('Sun') && !selected.includes('Sat');
            const isEveryday = days.every(d => selected.includes(d));
            let pattern = 'Custom';
            if (isEveryday) pattern = 'Everyday';
            else if (isWeekdays) pattern = 'Weekdays';
            setPatternBtn(pattern);
            updateCustomDays();
        });
    });

    // Initialize if editing
    if (isEdit && survey) {
        if (survey.autoSchedule) {
            document.getElementById('scheduleFields').style.display = 'block';
        }
        const hasDates = survey.startDate && survey.startDate !== '';
        const hasRepeat = survey.repeat && survey.repeat !== '' && survey.repeat !== 'Everyday' && survey.customDays && survey.customDays !== '';

        if (hasDates) {
            document.querySelector('.sched-type-btn[data-type="date"]')?.click();
        } else if (hasRepeat) {
            document.querySelector('.sched-type-btn[data-type="repeat"]')?.click();
            const dayOrder = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
            let activeDays = [];
            if (survey.repeat === 'Everyday') {
                activeDays = dayOrder.slice();
            } else if (survey.repeat === 'Weekdays') {
                activeDays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'];
            } else if (survey.repeat === 'Custom' && survey.customDays) {
                activeDays = survey.customDays.split(',').map(d => d.trim()).filter(d => dayOrder.includes(d));
            }
            activeDays.forEach(d => setDayBtn(d, true));
            setPatternBtn(survey.repeat || 'Everyday');
            updateCustomDays();
        }
    } else {
        // Default: Date Range for new survey
        document.querySelector('.sched-type-btn[data-type="date"]')?.click();
    }
}

// ============================================
// Handle Survey Save
// ============================================
async function handleSurveySave(isEdit, surveyName) {
    const name = document.getElementById('surveyNameInput')?.value?.trim();
    const description = document.getElementById('surveyDescInput')?.value?.trim();
    const accessLevel = document.getElementById('surveyAccessInput')?.value;
    const open = document.getElementById('surveyOpenInput')?.checked || false;
    const autoSchedule = document.getElementById('surveyAutoScheduleInput')?.checked || false;
    const startDate = document.getElementById('surveyStartDateInput')?.value || '';
    const startTime = document.getElementById('surveyStartTimeInput')?.value || '';
    const endDate = document.getElementById('surveyEndDateInput')?.value || '';
    const endTime = document.getElementById('surveyEndTimeInput')?.value || '';
    const repeat = document.getElementById('surveyRepeatInput')?.value || '';
    const customDays = document.getElementById('surveyCustomDaysInput')?.value || '';

    if (!name) {
        showToast('Survey name is required', 'error');
        return false;
    }

    if (autoSchedule) {
        const hasDates = startDate || endDate;
        const hasTimes = startTime && endTime;
        const hasRepeat = repeat && repeat !== '' && customDays && customDays !== '';
        if (!hasDates && !hasRepeat) {
            showToast('Auto Schedule requires either dates or a repeat pattern.', 'error');
            return false;
        }
        if (hasDates && !hasTimes) {
            showToast('Date Range requires both Start Time and End Time.', 'error');
            return false;
        }
    }

    const token = getToken();
    showToast(isEdit ? 'Updating survey...' : 'Creating survey...', 'info');

    try {
        const data = await saveSurvey(token, name, description, accessLevel, open, autoSchedule, startDate, startTime, endDate, endTime, repeat, customDays);
        if (data.error) {
            showToast('Save failed: ' + data.error, 'error');
            return false;
        }
        showToast(isEdit ? 'Survey updated!' : 'Survey created!', 'success');
        renderAdminPage();
        return true;
    } catch (err) {
        showToast('Save failed: ' + err.message, 'error');
        return false;
    }
}

// ============================================
// Delete Survey
// ============================================
async function deleteSurveyPrompt(name) {
    const confirmed = await showConfirm('Delete Survey', `Delete survey "${name}"?\n\nThis will also delete all questions. This cannot be undone.`);
    if (!confirmed) return;

    const token = getToken();
    showToast('Deleting survey...', 'info');

    try {
        const data = await deleteSurvey(token, name);
        if (data.error) {
            showToast('Delete failed: ' + data.error, 'error');
            return;
        }
        showToast('Survey deleted', 'success');
        // Update local state to avoid stale cache from backend
        currentSurveys = currentSurveys.filter(s => s.name !== name);
        // Re-render dashboard with updated local data
        const adminCheck = await checkAdmin(token);
        loadAdminDashboard(token, adminCheck, currentSurveys);
    } catch (err) {
        showToast('Delete failed: ' + err.message, 'error');
    }
}

// ============================================
// Duplicate Survey
// ============================================
async function duplicateSurvey(name) {
    const survey = currentSurveys.find(s => s.name === name);
    if (!survey) return;

    const newName = name + ' (Copy)';
    const token = getToken();
    showToast('Duplicating survey...', 'info');

    try {
        const saveRes = await saveSurvey(token, newName, survey.description, survey.accessLevel, false, survey.autoSchedule, survey.startDate, survey.startTime, survey.endDate, survey.endTime, survey.repeat || 'Everyday', survey.customDays || '');
        if (saveRes.error) {
            showToast('Duplicate failed: ' + saveRes.error, 'error');
            return;
        }

        const data = await getAdminEvents(token, name);
        if (data.error) {
            showToast('Duplicate failed: ' + data.error, 'error');
            return;
        }
        const events = data.events || [];

        for (const evt of events) {
            let eventOptions = [];
            if (evt.type === 'composite') {
                eventOptions = evt.subQuestions || [];
            } else {
                eventOptions = evt.options ? evt.options.map(stripHtml) : [];
            }
            const evtRes = await saveEvent(token, newName, evt.name, evt.type, evt.maxSelect, eventOptions, true, null, evt.required !== false);
            if (evtRes.error) {
                showToast('Duplicate failed copying questions: ' + evtRes.error, 'error');
                return;
            }
        }

        showToast('Survey duplicated!', 'success');
        renderAdminPage();
    } catch (err) {
        showToast('Duplicate failed: ' + err.message, 'error');
    }
}

// ============================================
// Preview Survey
// ============================================
async function previewSurvey(surveyName) {
    const modal = new Modal({
        title: 'Preview: ' + surveyName,
        content: '<div style="padding:16px;color:#6b7280;text-align:center;">Loading preview...</div>',
        confirmText: 'Close',
        onConfirm: () => modal.close()
    });
    modal.open();

    const token = getToken();
    try {
        const data = await getAdminEvents(token, surveyName);
        const events = data.events || [];

        if (events.length === 0) {
            modal.setContent('<p class="text-center">No questions in this survey.</p>');
            return;
        }

        let html = `<div class="preview-panel"><div class="preview-label">Member View</div>`;
        html += `<div style="font-weight:700;color:var(--primary);margin-bottom:16px;font-size:1.1rem;">${escapeHtml(surveyName)}</div>`;

        events.forEach((evt) => {
            html += `<div style="margin-bottom:16px;padding-bottom:12px;border-bottom:1px dashed #e2e8f0;">`;
            html += `<div style="font-weight:600;margin-bottom:8px;white-space:pre-wrap;">${escapeHtml(stripHtml(evt.name))}</div>`;

            if (evt.type === 'dropdown') {
                html += `<select disabled style="width:100%;padding:12px;"><option>- Select -</option>${evt.options.map(o => `<option>${o}</option>`).join('')}</select>`;
            } else if (evt.type === 'multi') {
                evt.options.forEach(opt => {
                    html += `<label style="display:flex;align-items:center;gap:8px;padding:8px;background:#f8fafc;border-radius:6px;margin-bottom:6px;"><input type="checkbox" disabled><span>${opt}</span></label>`;
                });
            } else if (evt.type === 'rank') {
                html += `<p style="font-size:0.85rem;color:var(--text-light);margin-bottom:6px;">Rank your top ${evt.maxSelect}</p>`;
                const ordinals = ['1st', '2nd', '3rd', '4th', '5th'];
                for (let r = 1; r <= evt.maxSelect; r++) {
                    const ord = ordinals[r - 1] || r + 'th';
                    html += `<div style="margin-bottom:6px;"><label style="font-size:0.85rem;font-weight:600;color:var(--primary);">${ord} Choice</label><select disabled style="width:100%;padding:10px;margin-top:4px;"><option>- Select -</option>${evt.options.map(o => `<option>${o}</option>`).join('')}</select></div>`;
                }
            } else if (evt.type === 'text') {
                html += `<input type="text" disabled placeholder="Type your answer..." style="width:100%;padding:12px;">`;
            } else if (evt.type === 'rating') {
                html += `<p style="font-size:0.85rem;color:var(--text-light);margin-bottom:6px;">10 = highest, 0 = lowest</p>`;
                html += `<select disabled style="width:100%;padding:12px;"><option>- Select -</option>${[10, 9, 8, 7, 6, 5, 4, 3, 2, 1, 0].map(r => `<option>${r}</option>`).join('')}</select>`;
            } else if (evt.type === 'composite') {
                html += `<div style="padding-left:12px;border-left:3px solid #e2e8f0;">`;
                if (evt.subQuestions && evt.subQuestions.length > 0) {
                    evt.subQuestions.forEach(sub => {
                        html += `<div style="margin-bottom:10px;">`;
                        html += `<label style="font-weight:600;font-size:0.9rem;color:#4b5563;display:block;margin-bottom:4px;">${escapeHtml(sub.label)} ${sub.required !== false ? '<span style="color:#dc2626;">*</span>' : ''}</label>`;
                        if (sub.type === 'text') {
                            html += `<input type="text" disabled placeholder="Text answer" style="width:100%;padding:10px;border:2px solid #e2e8f0;border-radius:6px;background:#f9fafb;">`;
                        } else if (sub.type === 'dropdown') {
                            html += `<select disabled style="width:100%;padding:10px;border:2px solid #e2e8f0;border-radius:6px;background:#f9fafb;"><option>- Select -</option>${(sub.options || []).map(o => `<option>${o}</option>`).join('')}</select>`;
                        } else if (sub.type === 'date') {
                            html += `<input type="text" disabled placeholder="Date" style="width:100%;padding:10px;border:2px solid #e2e8f0;border-radius:6px;background:#f9fafb;">`;
                        } else if (sub.type === 'rating') {
                            html += `<select disabled style="width:100%;padding:10px;border:2px solid #e2e8f0;border-radius:6px;background:#f9fafb;"><option>- Select -</option>${[10, 9, 8, 7, 6, 5, 4, 3, 2, 1, 0].map(r => `<option>${r}</option>`).join('')}</select>`;
                        }
                        html += `</div>`;
                    });
                }
                html += `</div>`;
            }

            html += `</div>`;
        });

        html += `<button class="btn btn-success" style="width:100%;margin-top:8px;opacity:0.6;" disabled>OK (Preview Only)</button>`;
        html += `</div>`;
        modal.setContent(html);
    } catch (err) {
        modal.setContent('<p class="text-center text-danger">Failed to load preview.</p>');
    }
}

// ============================================
// Question Modal (Create/Edit)
// ============================================
function openQuestionModal(surveyName, questionIdx = null) {
    const isEdit = questionIdx !== null;
    const q = isEdit ? currentQuestions[questionIdx] : null;
    currentSurveyName = surveyName;

    const modal = new Modal({
        title: isEdit ? 'Edit Question' : 'Add Question',
        content: buildQuestionModalContent(isEdit, q),
        confirmText: isEdit ? 'Save Changes' : 'Add Question',
        cancelText: 'Cancel',
        onConfirm: () => handleQuestionSave(isEdit, surveyName, questionIdx)
    });

    modal.open();

    // Initialize Quill after modal is visible
    setTimeout(() => {
        const editorEl = document.getElementById('quillEditor');
        if (editorEl && typeof Quill !== 'undefined') {
            if (quillEditor) {
                try { quillEditor.off('text-change'); } catch (e) {}
            }
            quillEditor = new Quill(editorEl, {
                theme: 'snow',
                placeholder: 'Type your question here...',
                modules: {
                    toolbar: [
                        [{ header: [1, 2, false] }],
                        ['bold', 'italic', 'underline'],
                        [{ size: ['small', false, 'large', 'huge'] }],
                        [{ color: [] }, { background: [] }],
                        ['clean']
                    ]
                }
            });

            if (q && q.name) {
                quillEditor.setText(stripHtml(q.name));
            }
        }

        // Setup type change handler
        document.getElementById('questionTypeInput')?.addEventListener('change', onQuestionTypeChange);
        document.getElementById('btnAddOption')?.addEventListener('click', addOptionInput);
        document.getElementById('btnAddSubQuestion')?.addEventListener('click', addSubQuestionInput);
        onQuestionTypeChange();

        // If editing, restore options
        if (isEdit && q && q.options && q.options.length > 0) {
            const builder = document.getElementById('optionsBuilder');
            if (builder) {
                builder.innerHTML = q.options.map((opt, i) => `
                    <div class="option-input-row">
                        <input type="text" value="${escapeHtml(stripHtml(opt))}" placeholder="Option ${i + 1}">
                        <button class="btn-remove-option" type="button">&times;</button>
                    </div>
                `).join('');
                builder.querySelectorAll('.btn-remove-option').forEach(btn => {
                    btn.addEventListener('click', () => {
                        btn.closest('.option-input-row').remove();
                    });
                });
            }
        }
    }, 200);
}

// ============================================
// Build Question Modal Content
// ============================================
function buildQuestionModalContent(isEdit, q) {
    return `
        <div class="form-group">
            <label>Question Text</label>
            <div id="quillEditor" style="min-height:120px;"></div>
            <p class="form-hint">Use the toolbar for bold, italic, and font size.</p>
        </div>
        <div class="form-group">
            <label>Question Type</label>
            <select id="questionTypeInput">
                <option value="" disabled ${!q ? 'selected' : ''}>- Choose question type -</option>
                <option value="dropdown" ${q && q.type === 'dropdown' ? 'selected' : ''}>Dropdown (single choice)</option>
                <option value="multi" ${q && q.type === 'multi' ? 'selected' : ''}>Multi-Select (checkboxes)</option>
                <option value="rank" ${q && q.type === 'rank' ? 'selected' : ''}>Ranked Choice</option>
                <option value="text" ${q && q.type === 'text' ? 'selected' : ''}>Text Input</option>
                <option value="rating" ${q && q.type === 'rating' ? 'selected' : ''}>Rating (0-10)</option>
                <option value="date" ${q && q.type === 'date' ? 'selected' : ''}>Date Picker</option>
                <option value="composite" ${q && q.type === 'composite' ? 'selected' : ''}>Composite Group</option>
            </select>
        </div>
        <div class="form-group" id="maxSelectGroup" style="display:${q && (q.type === 'multi' || q.type === 'rank') ? 'block' : 'none'};">
            <label>Max Selections / Ranks</label>
            <input type="number" id="maxSelectInput" value="${q ? q.maxSelect : 3}" min="1" max="10">
        </div>
        <div class="form-group" id="optionsGroup" style="display:${q && q.type !== 'text' && q.type !== 'rating' && q.type !== 'date' && q.type !== 'composite' ? 'block' : 'none'};">
            <label>Options</label>
            <div class="options-builder" id="optionsBuilder">
                ${renderOptionInputs(q && q.options ? q.options : [])}
            </div>
            <button class="btn-add-option" type="button" id="btnAddOption">+ Add Option</button>
        </div>
        <div class="form-group" id="compositeGroup" style="display:${q && q.type === 'composite' ? 'block' : 'none'};">
            <label>Sub-Questions</label>
            <div id="compositeBuilder">
                ${renderCompositeSubQuestions(q && q.subQuestions ? q.subQuestions : [])}
            </div>
            <button class="btn-add-option" type="button" id="btnAddSubQuestion">+ Add Sub-Question</button>
        </div>
        <div class="form-group" id="dateFormatGroup" style="display:${q && q.type === 'date' ? 'block' : 'none'};">
            <label>Date Format</label>
            <select id="dateFormatInput">
                <option value="mm/dd/yyyy" ${q && q.type === 'date' && q.dateFormat === 'mm/dd/yyyy' ? 'selected' : ''}>MM/DD/YYYY</option>
                <option value="dd/mm/yyyy" ${q && q.type === 'date' && q.dateFormat === 'dd/mm/yyyy' ? 'selected' : ''}>DD/MM/YYYY</option>
                <option value="yyyy/mm/dd" ${q && q.type === 'date' && q.dateFormat === 'yyyy/mm/dd' ? 'selected' : ''}>YYYY/MM/DD</option>
                <option value="mm-dd-yyyy" ${q && q.type === 'date' && q.dateFormat === 'mm-dd-yyyy' ? 'selected' : ''}>MM-DD-YYYY</option>
                <option value="dd-mm-yyyy" ${q && q.type === 'date' && q.dateFormat === 'dd-mm-yyyy' ? 'selected' : ''}>DD-MM-YYYY</option>
                <option value="yyyy-mm-dd" ${q && q.type === 'date' && q.dateFormat === 'yyyy-mm-dd' ? 'selected' : ''}>YYYY-MM-DD</option>
            </select>
        </div>
        <div class="toggle-row">
            <span class="toggle-label">Required</span>
            <input type="checkbox" class="toggle-switch" id="questionRequiredInput" ${!q || q.required !== false ? 'checked' : ''}>
        </div>
        <div class="toggle-row">
            <span class="toggle-label">Show Question</span>
            <input type="checkbox" class="toggle-switch" id="questionShowInput" ${!q || q.show !== false ? 'checked' : ''}>
        </div>
    `;
}

// ============================================
// Render Option Inputs
// ============================================
function renderOptionInputs(options) {
    if (!options || options.length === 0) {
        return `
            <div class="option-input-row"><input type="text" placeholder="Option 1"><button class="btn-remove-option" type="button">&times;</button></div>
            <div class="option-input-row"><input type="text" placeholder="Option 2"><button class="btn-remove-option" type="button">&times;</button></div>
        `;
    }
    return options.map((opt, i) => `
        <div class="option-input-row">
            <input type="text" value="${escapeHtml(stripHtml(opt))}" placeholder="Option ${i + 1}">
            <button class="btn-remove-option" type="button">&times;</button>
        </div>
    `).join('');
}

// ============================================
// Render Composite Sub-Questions
// ============================================
function renderCompositeSubQuestions(subs) {
    if (!subs || subs.length === 0) {
        return `
            <div class="composite-sub-row" data-index="0" style="display:flex;gap:8px;align-items:flex-start;margin-bottom:8px;padding:10px;background:#f8fafc;border-radius:8px;flex-wrap:wrap;">
                <input type="text" class="composite-sub-label" placeholder="Sub-question label" style="flex:2;padding:8px;border:2px solid #d1d5db;border-radius:6px;min-width:160px;">
                <select class="composite-sub-type" style="flex:1;padding:8px;border:2px solid #d1d5db;border-radius:6px;min-width:100px;">
                    <option value="text">Text</option>
                    <option value="dropdown">Dropdown</option>
                    <option value="date">Date</option>
                    <option value="rating">Rating</option>
                </select>
                <label style="display:flex;align-items:center;gap:4px;font-size:0.85rem;white-space:nowrap;">
                    <input type="checkbox" class="composite-sub-required" checked> Required
                </label>
                <div class="composite-sub-options" style="display:none;flex-basis:100%;margin-top:6px;"></div>
                <button class="btn-remove-option btn-remove-sub" type="button" style="width:32px;height:32px;border-radius:6px;">&times;</button>
            </div>
        `;
    }
    return subs.map((sub, i) => {
        const optionsHtml = (sub.type === 'dropdown' && sub.options && sub.options.length > 0)
            ? `<div class="composite-sub-options" style="display:block;flex-basis:100%;margin-top:6px;">
                ${sub.options.map((opt, oi) => `
                    <div class="option-input-row" style="display:flex;gap:6px;align-items:center;margin-bottom:4px;">
                        <input type="text" value="${escapeHtml(stripHtml(opt))}" placeholder="Option ${oi + 1}" style="flex:1;padding:6px;border:2px solid #d1d5db;border-radius:6px;">
                        <button class="btn-remove-option btn-remove-sub-opt" type="button" style="width:28px;height:28px;border-radius:6px;">&times;</button>
                    </div>
                `).join('')}
                <button class="btn-add-sub-opt" type="button" style="width:100%;padding:8px;margin-top:4px;background:#f1f5f9;border:2px dashed #cbd5e1;border-radius:6px;color:#475569;font-size:0.85rem;cursor:pointer;text-align:center;">+ Add option here</button>
               </div>`
            : `<div class="composite-sub-options" style="display:${sub.type === 'dropdown' ? 'block' : 'none'};flex-basis:100%;margin-top:6px;"></div>`;
        return `
            <div class="composite-sub-row" data-index="${i}" style="display:flex;gap:8px;align-items:flex-start;margin-bottom:8px;padding:10px;background:#f8fafc;border-radius:8px;flex-wrap:wrap;">
                <input type="text" class="composite-sub-label" value="${escapeHtml(stripHtml(sub.label || ''))}" placeholder="Sub-question label" style="flex:2;padding:8px;border:2px solid #d1d5db;border-radius:6px;min-width:160px;">
                <select class="composite-sub-type" style="flex:1;padding:8px;border:2px solid #d1d5db;border-radius:6px;min-width:100px;">
                    <option value="text" ${sub.type === 'text' ? 'selected' : ''}>Text</option>
                    <option value="dropdown" ${sub.type === 'dropdown' ? 'selected' : ''}>Dropdown</option>
                    <option value="date" ${sub.type === 'date' ? 'selected' : ''}>Date</option>
                    <option value="rating" ${sub.type === 'rating' ? 'selected' : ''}>Rating</option>
                </select>
                <label style="display:flex;align-items:center;gap:4px;font-size:0.85rem;white-space:nowrap;">
                    <input type="checkbox" class="composite-sub-required" ${sub.required !== false ? 'checked' : ''}> Required
                </label>
                ${optionsHtml}
                <button class="btn-remove-option btn-remove-sub" type="button" style="width:32px;height:32px;border-radius:6px;">&times;</button>
            </div>
        `;
    }).join('');
}

// ============================================
// Question Type Change Handler
// ============================================
function onQuestionTypeChange() {
    const type = document.getElementById('questionTypeInput')?.value;
    const maxGroup = document.getElementById('maxSelectGroup');
    const optsGroup = document.getElementById('optionsGroup');
    const builder = document.getElementById('optionsBuilder');
    const dateFormatGroup = document.getElementById('dateFormatGroup');
    const compositeGroup = document.getElementById('compositeGroup');
    const compositeBuilder = document.getElementById('compositeBuilder');

    if (type === 'multi' || type === 'rank') {
        if (maxGroup) maxGroup.style.display = 'block';
    } else {
        if (maxGroup) maxGroup.style.display = 'none';
    }

    if (type === 'text' || type === 'rating' || type === 'date' || type === 'composite') {
        if (optsGroup) optsGroup.style.display = 'none';
        if (builder) builder.innerHTML = '';
    } else {
        if (optsGroup) optsGroup.style.display = 'block';
        if (builder && builder.children.length === 0) {
            builder.innerHTML = `
                <div class="option-input-row"><input type="text" placeholder="Option 1"><button class="btn-remove-option" type="button">&times;</button></div>
                <div class="option-input-row"><input type="text" placeholder="Option 2"><button class="btn-remove-option" type="button">&times;</button></div>
            `;
            builder.querySelectorAll('.btn-remove-option').forEach(btn => {
                btn.addEventListener('click', () => {
                    btn.closest('.option-input-row').remove();
                });
            });
        }
    }

    if (type === 'date') {
        if (dateFormatGroup) dateFormatGroup.style.display = 'block';
    } else {
        if (dateFormatGroup) dateFormatGroup.style.display = 'none';
    }

    if (type === 'composite') {
        if (compositeGroup) compositeGroup.style.display = 'block';
        if (compositeBuilder) {
            if (compositeBuilder.children.length === 0) {
                compositeBuilder.innerHTML = renderCompositeSubQuestions([]);
            }
            attachCompositeRemoveListeners();
        }
    } else {
        if (compositeGroup) compositeGroup.style.display = 'none';
    }
}

// ============================================
// Add Sub-Question Input
// ============================================
function addSubQuestionInput() {
    const builder = document.getElementById('compositeBuilder');
    if (!builder) return;
    const count = builder.querySelectorAll('.composite-sub-row').length;
    const div = document.createElement('div');
    div.className = 'composite-sub-row';
    div.dataset.index = count;
    div.style.cssText = 'display:flex;gap:8px;align-items:flex-start;margin-bottom:8px;padding:10px;background:#f8fafc;border-radius:8px;flex-wrap:wrap;';
    div.innerHTML = `
        <input type="text" class="composite-sub-label" placeholder="Sub-question label" style="flex:2;padding:8px;border:2px solid #d1d5db;border-radius:6px;min-width:160px;">
        <select class="composite-sub-type" style="flex:1;padding:8px;border:2px solid #d1d5db;border-radius:6px;min-width:100px;">
            <option value="text">Text</option>
            <option value="dropdown">Dropdown</option>
            <option value="date">Date</option>
            <option value="rating">Rating</option>
        </select>
        <label style="display:flex;align-items:center;gap:4px;font-size:0.85rem;white-space:nowrap;">
            <input type="checkbox" class="composite-sub-required" checked> Required
        </label>
        <div class="composite-sub-options" style="display:none;flex-basis:100%;margin-top:6px;"></div>
        <button class="btn-remove-option btn-remove-sub" type="button" style="width:32px;height:32px;border-radius:6px;">&times;</button>
    `;
    builder.appendChild(div);
    const typeSelect = div.querySelector('.composite-sub-type');
    const optionsDiv = div.querySelector('.composite-sub-options');
    const updateOptionsVisibility = () => {
        if (optionsDiv) {
            optionsDiv.style.display = typeSelect.value === 'dropdown' ? 'block' : 'none';
        }
        if (typeSelect.value === 'dropdown' && optionsDiv && optionsDiv.children.length === 0) {
            optionsDiv.innerHTML = `
                <div class="option-input-row" style="display:flex;gap:6px;align-items:center;margin-bottom:4px;">
                    <input type="text" placeholder="Option 1" style="flex:1;padding:6px;border:2px solid #d1d5db;border-radius:6px;">
                    <button class="btn-remove-option btn-remove-sub-opt" type="button" style="width:28px;height:28px;border-radius:6px;">&times;</button>
                </div>
                <div class="option-input-row" style="display:flex;gap:6px;align-items:center;margin-bottom:4px;">
                    <input type="text" placeholder="Option 2" style="flex:1;padding:6px;border:2px solid #d1d5db;border-radius:6px;">
                    <button class="btn-remove-option btn-remove-sub-opt" type="button" style="width:28px;height:28px;border-radius:6px;">&times;</button>
                </div>
                <button class="btn-add-sub-opt" type="button" style="width:100%;padding:8px;margin-top:4px;background:#f1f5f9;border:2px dashed #cbd5e1;border-radius:6px;color:#475569;font-size:0.85rem;cursor:pointer;text-align:center;">+ Add option here</button>
            `;
            optionsDiv.querySelectorAll('.btn-remove-sub-opt').forEach(btn => {
                btn.addEventListener('click', () => btn.closest('.option-input-row').remove());
            });
            optionsDiv.querySelector('.btn-add-sub-opt').addEventListener('click', () => {
                const row = document.createElement('div');
                row.className = 'option-input-row';
                row.style.cssText = 'display:flex;gap:6px;align-items:center;margin-bottom:4px;';
                const count = optionsDiv.querySelectorAll('.option-input-row').length;
                row.innerHTML = `
                    <input type="text" placeholder="Option ${count}" style="flex:1;padding:6px;border:2px solid #d1d5db;border-radius:6px;">
                    <button class="btn-remove-option btn-remove-sub-opt" type="button" style="width:28px;height:28px;border-radius:6px;">&times;</button>
                `;
                optionsDiv.insertBefore(row, optionsDiv.querySelector('.btn-add-sub-opt'));
                row.querySelector('.btn-remove-sub-opt').addEventListener('click', () => row.remove());
            });
        }
    };
    typeSelect.addEventListener('change', updateOptionsVisibility);
    updateOptionsVisibility();
    div.querySelector('.btn-remove-sub').addEventListener('click', () => {
        div.remove();
    });
}

function attachCompositeRemoveListeners() {
    document.querySelectorAll('.btn-remove-sub').forEach(btn => {
        btn.addEventListener('click', () => {
            btn.closest('.composite-sub-row').remove();
        });
    });
    document.querySelectorAll('.composite-sub-type').forEach(select => {
        select.addEventListener('change', () => {
            const row = select.closest('.composite-sub-row');
            if (!row) return;
            const optionsDiv = row.querySelector('.composite-sub-options');
            if (!optionsDiv) return;
            const isDropdown = select.value === 'dropdown';
            optionsDiv.style.display = isDropdown ? 'block' : 'none';
            if (isDropdown && optionsDiv.children.length === 0) {
                optionsDiv.innerHTML = `
                    <div class="option-input-row" style="display:flex;gap:6px;align-items:center;margin-bottom:4px;">
                        <input type="text" placeholder="Option 1" style="flex:1;padding:6px;border:2px solid #d1d5db;border-radius:6px;">
                        <button class="btn-remove-option btn-remove-sub-opt" type="button" style="width:28px;height:28px;border-radius:6px;">&times;</button>
                    </div>
                    <div class="option-input-row" style="display:flex;gap:6px;align-items:center;margin-bottom:4px;">
                        <input type="text" placeholder="Option 2" style="flex:1;padding:6px;border:2px solid #d1d5db;border-radius:6px;">
                        <button class="btn-remove-option btn-remove-sub-opt" type="button" style="width:28px;height:28px;border-radius:6px;">&times;</button>
                    </div>
                    <button class="btn-add-sub-opt" type="button" style="width:100%;padding:8px;margin-top:4px;background:#f1f5f9;border:2px dashed #cbd5e1;border-radius:6px;color:#475569;font-size:0.85rem;cursor:pointer;text-align:center;">+ Add option here</button>
                `;
                optionsDiv.querySelectorAll('.btn-remove-sub-opt').forEach(btn => {
                    btn.addEventListener('click', () => btn.closest('.option-input-row').remove());
                });
                optionsDiv.querySelector('.btn-add-sub-opt').addEventListener('click', () => {
                    const row = document.createElement('div');
                    row.className = 'option-input-row';
                    row.style.cssText = 'display:flex;gap:6px;align-items:center;margin-bottom:4px;';
                    const count = optionsDiv.querySelectorAll('.option-input-row').length;
                    row.innerHTML = `
                        <input type="text" placeholder="Option ${count}" style="flex:1;padding:6px;border:2px solid #d1d5db;border-radius:6px;">
                        <button class="btn-remove-option btn-remove-sub-opt" type="button" style="width:28px;height:28px;border-radius:6px;">&times;</button>
                    `;
                    optionsDiv.insertBefore(row, optionsDiv.querySelector('.btn-add-sub-opt'));
                    row.querySelector('.btn-remove-sub-opt').addEventListener('click', () => row.remove());
                });
            }
        });
    });
    document.querySelectorAll('.btn-remove-sub-opt').forEach(btn => {
        btn.addEventListener('click', () => {
            btn.closest('.option-input-row').remove();
        });
    });
    document.querySelectorAll('.btn-add-sub-opt').forEach(btn => {
        btn.addEventListener('click', () => {
            const optionsDiv = btn.closest('.composite-sub-options');
            if (!optionsDiv) return;
            const row = document.createElement('div');
            row.className = 'option-input-row';
            row.style.cssText = 'display:flex;gap:6px;align-items:center;margin-bottom:4px;';
            const count = optionsDiv.querySelectorAll('.option-input-row').length;
            row.innerHTML = `
                <input type="text" placeholder="Option ${count}" style="flex:1;padding:6px;border:2px solid #d1d5db;border-radius:6px;">
                <button class="btn-remove-option btn-remove-sub-opt" type="button" style="width:28px;height:28px;border-radius:6px;">&times;</button>
            `;
            optionsDiv.insertBefore(row, optionsDiv.querySelector('.btn-add-sub-opt'));
            row.querySelector('.btn-remove-sub-opt').addEventListener('click', () => row.remove());
        });
    });
}

// ============================================
// Add Option Input
// ============================================
function addOptionInput() {
    const builder = document.getElementById('optionsBuilder');
    if (!builder) return;
    const count = builder.querySelectorAll('.option-input-row').length;
    const div = document.createElement('div');
    div.className = 'option-input-row';
    div.innerHTML = `
        <input type="text" placeholder="Option ${count + 1}">
        <button class="btn-remove-option" type="button">&times;</button>
    `;
    builder.appendChild(div);
    div.querySelector('.btn-remove-option').addEventListener('click', () => {
        div.remove();
    });
}

// ============================================
// Handle Question Save
// ============================================
async function handleQuestionSave(isEdit, surveyName, questionIdx) {
    const eventName = quillEditor ? quillEditor.getText().trim() : '';
    const type = document.getElementById('questionTypeInput')?.value;
    const maxSelect = parseInt(document.getElementById('maxSelectInput')?.value || '1', 10);
    const show = document.getElementById('questionShowInput')?.checked || false;
    const required = document.getElementById('questionRequiredInput')?.checked || false;
    const dateFormat = document.getElementById('dateFormatInput')?.value;

    if (!eventName) {
        showToast('Question text is required', 'error');
        return false;
    }

    let options = [];
    if (type === 'composite') {
        const subRows = document.querySelectorAll('#compositeBuilder .composite-sub-row');
        const subs = [];
        subRows.forEach(row => {
            const label = row.querySelector('.composite-sub-label')?.value?.trim() || '';
            const subType = row.querySelector('.composite-sub-type')?.value || 'text';
            const subRequired = row.querySelector('.composite-sub-required')?.checked !== false;
            if (!label) return;
            const sub = { label, type: subType, required: subRequired };
            if (subType === 'dropdown') {
                const optInputs = row.querySelectorAll('.composite-sub-options input[type="text"]');
                sub.options = Array.from(optInputs).map(inp => inp.value.trim()).filter(v => v);
                if (sub.options.length < 2) {
                    sub.options = [];
                }
            }
            subs.push(sub);
        });
        const hasEmptyDropdown = subs.some(sub => sub.type === 'dropdown' && sub.options.length < 2);
        if (hasEmptyDropdown) {
            showToast('Dropdown sub-questions need at least 2 options', 'error');
            return false;
        }
        if (subs.length === 0) {
            showToast('Add at least 1 sub-question for composite', 'error');
            return false;
        }
        options = subs;
    } else if (type !== 'text' && type !== 'rating' && type !== 'date') {
        const inputs = document.querySelectorAll('#optionsBuilder input');
        options = Array.from(inputs).map(inp => inp.value.trim()).filter(v => v);
        if (options.length < 2) {
            showToast('At least 2 options required', 'error');
            return false;
        }
    }

    const token = getToken();
    const q = isEdit ? currentQuestions[questionIdx] : null;
    const payload = {
        token,
        surveyName,
        eventName,
        type,
        maxSelect,
        options,
        show,
        required,
        dateFormat: type === 'date' ? dateFormat : null,
        rowIndex: isEdit ? q.rowIndex : null
    };

    showToast(isEdit ? 'Updating question...' : 'Adding question...', 'info');

    try {
        const data = await saveEvent(token, surveyName, eventName, type, maxSelect, options, show, payload.rowIndex, required, type === 'date' ? dateFormat : null);
        if (data.error) {
            showToast('Save failed: ' + data.error, 'error');
            return false;
        }
        showToast(isEdit ? 'Question updated!' : 'Question added!', 'success');
        
        // Always refresh question list from backend
        try {
            const token = getToken();
            const freshData = await getAdminEvents(token, surveyName);
            currentQuestions = freshData.events || [];
        } catch (e) {
            console.warn('Failed to refresh questions after save:', e);
        }
        
        // Refresh question list if panel is open
        const expandedEl = document.querySelector(`#expanded-${CSS.escape(surveyName)}`);
        if (expandedEl && !expandedEl.classList.contains('hidden')) {
            const panel = expandedEl.querySelector('.questions-panel');
            if (panel && !panel.classList.contains('hidden')) {
                renderQuestionList(panel, surveyName, currentQuestions);
            }
        }
        return true;
    } catch (err) {
        showToast('Save failed: ' + err.message, 'error');
        return false;
    }
}

// ============================================
// Delete Question
// ============================================
async function deleteQuestionPrompt(surveyName, idx) {
    const q = currentQuestions[idx];
    if (!q) return;

    const confirmed = await showConfirm('Delete Question', `Delete question "${stripHtml(q.name)}"?`);
    if (!confirmed) return;

    const token = getToken();
    showToast('Deleting question...', 'info');

    try {
        const data = await deleteEvent(token, surveyName, q.rowIndex);
        if (data.error) {
            showToast('Delete failed: ' + data.error, 'error');
            return;
        }
        showToast('Question deleted', 'success');
        // Update local state to avoid stale cache from backend
        currentQuestions = currentQuestions.filter((_, i) => i !== idx);
        const expandedEl = document.querySelector(`#expanded-${CSS.escape(surveyName)}`);
        if (expandedEl && !expandedEl.classList.contains('hidden')) {
            const panel = expandedEl.querySelector('.questions-panel');
            if (panel && !panel.classList.contains('hidden')) {
                renderQuestionList(panel, surveyName, currentQuestions);
            }
        }
    } catch (err) {
        showToast('Delete failed: ' + err.message, 'error');
    }
}

// ============================================
// Report Modal
// ============================================
function openReportModal(viewMode = 'today', filterSurvey = null, dateFrom = null, dateTo = null) {
    currentReportDateMode = viewMode;
    currentReportFilter = filterSurvey;
    currentReportDateFrom = dateFrom;
    currentReportDateTo = dateTo;

    const titleSuffix = filterSurvey ? `: ${filterSurvey}` : '';
    const modal = new Modal({
        title: 'Reports' + titleSuffix,
        content: '<div style="padding:16px;color:#6b7280;text-align:center;">Loading reports...</div>',
        footer: `
            <button class="btn btn-secondary" id="btnCloseReport">Close</button>
            <button class="btn btn-primary" id="btnExportCsv">📥 Export CSV</button>
        `
    });
    modal.open();

    document.getElementById('btnCloseReport')?.addEventListener('click', () => modal.close());

    const token = getToken();
    const payload = { adminToken: token, viewMode: viewMode };
    if (filterSurvey) payload.surveyName = filterSurvey;
    if (dateFrom) payload.dateFrom = dateFrom;
    if (dateTo) payload.dateTo = dateTo;

    adminSummary(token, viewMode, filterSurvey, dateFrom, dateTo)
        .then(data => {
            const dates = data.dates || {};
            const allDateKeys = Object.keys(dates).sort();
            currentReportData = dates;

            // Build survey list
            const allSurveyNames = new Set();
            currentSurveys.forEach(sv => allSurveyNames.add(sv.name));
            allDateKeys.forEach(dateKey => {
                Object.keys(dates[dateKey] || {}).forEach(svName => allSurveyNames.add(svName));
            });

            let html = '';

            // Survey filter dropdown
            html += `<div class="form-group" style="margin-bottom:12px;">`;
            html += `<label style="font-size:0.8rem;color:#6b7280;display:block;margin-bottom:4px;">Survey</label>`;
            html += `<select id="reportSurveyFilter" style="width:100%;padding:8px;border:1px solid #d1d5db;border-radius:6px;font-size:0.9rem;">`;
            html += `<option value="" ${!filterSurvey ? 'selected' : ''}>ALL</option>`;
            html += `<option disabled>--------------</option>`;
            [...allSurveyNames].sort().forEach(name => {
                html += `<option value="${escapeHtml(name)}" ${name === filterSurvey ? 'selected' : ''}>${escapeHtml(name)}</option>`;
            });
            html += `</select></div>`;

            // Date controls
            const today = new Date().toISOString().split('T')[0];
            const isToday = viewMode === 'today';
            html += `<div class="date-filter-bar" style="display:flex;gap:8px;align-items:flex-end;flex-wrap:wrap;margin-bottom:12px;padding:12px;background:#f8fafc;border-radius:8px;">`;
            html += `<button class="btn btn-secondary btn-sm ${isToday ? 'btn-primary' : ''}" id="btnReportToday" style="padding:8px 12px;">Today</button>`;
            html += `<div style="flex:1;min-width:120px;">`;
            html += `<label style="font-size:0.8rem;color:#6b7280;display:block;margin-bottom:4px;">From</label>`;
            html += `<input type="date" id="reportDateFrom" value="${escapeHtml(dateFrom || today)}" style="width:100%;padding:8px;border:1px solid #d1d5db;border-radius:6px;font-size:0.9rem;">`;
            html += `</div>`;
            html += `<div style="flex:1;min-width:120px;">`;
            html += `<label style="font-size:0.8rem;color:#6b7280;display:block;margin-bottom:4px;">To</label>`;
            html += `<input type="date" id="reportDateTo" value="${escapeHtml(dateTo || today)}" style="width:100%;padding:8px;border:1px solid #d1d5db;border-radius:6px;font-size:0.9rem;">`;
            html += `</div>`;
            html += `<button class="btn btn-primary btn-sm" id="btnReportApply" style="padding:8px 12px;">Apply</button>`;
            html += `</div>`;

            if (allDateKeys.length === 0) {
                html += `<div class="error-box" style="margin-top:16px;"><div class="error-icon">📅</div><p class="error-text">No submissions found for this view.</p></div>`;
            } else {
                html += `<div class="report-panel">`;

                // Group by survey
                const surveyGroups = {};
                allDateKeys.forEach(dateKey => {
                    const dayData = dates[dateKey] || {};
                    Object.entries(dayData).forEach(([svName, count]) => {
                        if (!surveyGroups[svName]) surveyGroups[svName] = {};
                        surveyGroups[svName][dateKey] = count;
                    });
                });

                const targetSurveys = filterSurvey ? { [filterSurvey]: surveyGroups[filterSurvey] || {} } : surveyGroups;

                Object.entries(targetSurveys).forEach(([svName, dateCounts]) => {
                    const datesForSurvey = Object.keys(dateCounts).sort();
                    const surveyTotal = Object.values(dateCounts).reduce((a, b) => a + b, 0);

                    html += `<div class="report-survey-header">${escapeHtml(svName)} <span style="font-weight:400;font-size:0.9rem;color:var(--text-light);">(${surveyTotal})</span></div>`;

                    datesForSurvey.forEach(dateKey => {
                        const count = dateCounts[dateKey];
                        html += `<div class="report-stat-row" style="padding-left:16px;">`;
                        html += `<span style="color:#6b7280;font-size:0.9rem;">${escapeHtml(dateKey)}</span>`;
                        html += `<span class="report-stat-count" data-survey="${escapeHtml(svName)}" data-date="${escapeHtml(dateKey)}">${count}</span>`;
                        html += `</div>`;
                        html += `<div class="names-panel hidden" style="background:#f8fafc;border-left:3px solid var(--primary-light);margin:0 0 8px 28px;border-radius:0 8px 8px 0;max-height:200px;overflow-y:auto;">`;
                        html += `<div class="name-row" style="padding:8px 12px;color:var(--text-light);font-size:0.85rem;">Click to load names...</div>`;
                        html += `</div>`;
                    });
                });

                html += `</div>`;

                if (viewMode !== 'today' && data.total) {
                    html += `<div style="margin-top:12px;padding:12px;background:#f1f5f9;border-radius:8px;font-weight:600;font-size:0.95rem;">Total: ${data.total}</div>`;
                }
            }

            modal.setContent(html);

            // Attach handlers
            document.getElementById('btnReportToday')?.addEventListener('click', () => {
                const filter = document.getElementById('reportSurveyFilter')?.value || null;
                const t = new Date().toISOString().split('T')[0];
                modal.close();
                openReportModal('today', filter, t, t);
            });

            document.getElementById('btnReportApply')?.addEventListener('click', () => {
                const from = document.getElementById('reportDateFrom')?.value || null;
                const to = document.getElementById('reportDateTo')?.value || null;
                const filter = document.getElementById('reportSurveyFilter')?.value || null;
                modal.close();
                openReportModal('fromto', filter, from, to);
            });

            document.getElementById('reportSurveyFilter')?.addEventListener('change', () => {
                const val = document.getElementById('reportSurveyFilter')?.value || null;
                const from = document.getElementById('reportDateFrom')?.value || null;
                const to = document.getElementById('reportDateTo')?.value || null;
                modal.close();
                openReportModal(currentReportDateMode, val, from, to);
            });

            // Click handlers for expandable names
            document.querySelectorAll('.report-stat-count').forEach(el => {
                el.addEventListener('click', async () => {
                    const panel = el.closest('.report-stat-row')?.nextElementSibling;
                    if (!panel) return;
                    if (panel.classList.contains('hidden')) {
                        if (!panel.dataset.loaded) {
                            const t = getToken();
                            try {
                                const data = await adminChoiceDetail(t, el.dataset.survey, '__ALL__', '__ALL__', el.dataset.date);
                                if (data.names && data.names.length > 0) {
                                    panel.innerHTML = data.names.map(n => `
                                        <div class="name-row" style="display:flex;justify-content:space-between;padding:8px 12px;border-bottom:1px solid #e2e8f0;font-size:0.85rem;">
                                            <span>${escapeHtml(n.fullName)}${n.event ? ' <span style="color:#6b7280;font-size:0.75rem;">(' + escapeHtml(n.event) + ' / ' + escapeHtml(n.choice) + ')</span>' : ''}</span>
                                            <span style="color:var(--text-light);font-size:0.75rem;">${new Date(n.timestamp).toLocaleString()}</span>
                                        </div>
                                    `).join('');
                                } else {
                                    panel.innerHTML = '<div style="padding:8px 12px;color:#6b7280;">No names found.</div>';
                                }
                                panel.dataset.loaded = 'true';
                            } catch (err) {
                                panel.innerHTML = '<div style="padding:8px 12px;color:#dc2626;">Failed to load names</div>';
                            }
                        }
                        panel.classList.remove('hidden');
                    } else {
                        panel.classList.add('hidden');
                    }
                });
            });

            document.getElementById('btnExportCsv')?.addEventListener('click', () => {
                exportCsv(currentReportData, currentReportFilter || 'all', currentReportDateFrom, currentReportDateTo);
            });

        })
        .catch(() => {
            modal.setContent('<p class="text-center text-danger">Failed to load report.</p>');
        });
}

// ============================================
// Export CSV
// ============================================
async function exportCsv(dates, filename, dateFrom = null, dateTo = null) {
    showToast('Generating CSV report...', 'info');
    let csv = 'Date,Survey,Count\n';
    const token = getToken();

    // Summary rows
    Object.entries(dates).sort((a, b) => a[0].localeCompare(b[0])).forEach(([dateKey, dayData]) => {
        Object.entries(dayData).forEach(([svName, count]) => {
            csv += `"${dateKey}","${escapeCsvField(svName)}",${count}\n`;
        });
    });

    csv += '\n--- Detail Rows ---\nDate,Survey,Full Name,Event,Choice,Timestamp\n';

    // Fetch detail rows
    for (const [dateKey, dayData] of Object.entries(dates).sort((a, b) => a[0].localeCompare(b[0]))) {
        for (const [svName, count] of Object.entries(dayData)) {
            try {
                const data = await adminChoiceDetail(token, svName, '__ALL__', '__ALL__', dateKey);
                if (data.names && data.names.length > 0) {
                    data.names.forEach(n => {
                        csv += `"${dateKey}","${escapeCsvField(svName)}","${escapeCsvField(n.fullName)}","${escapeCsvField(n.event || '')}","${escapeCsvField(n.choice || '')}","${new Date(n.timestamp).toLocaleString()}"\n`;
                    });
                }
            } catch (err) {
                console.error('CSV detail fetch failed', err);
            }
        }
    }

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const now = new Date();
    const pad = (n) => String(n).padStart(2, '0');
    const ts = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}-${pad(now.getHours())}-${pad(now.getMinutes())}-${pad(now.getSeconds())}`;
    a.download = `ROLC_Survey_Report_${ts}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    showToast('CSV exported!', 'success');
}

function escapeCsvField(str) {
    if (!str) return '';
    const s = String(str).replace(/"/g, '""');
    if (s.includes(',') || s.includes('\n') || s.includes('"')) return `"${s}"`;
    return s;
}

// ============================================
// Bulk Import Modal
// ============================================
function openBulkImportModal() {
    const modal = new Modal({
        title: 'Bulk Import Members',
        content: `
            <div class="form-group">
                <label>Paste member list</label>
                <textarea id="bulkImportArea" rows="10" style="width:100%;padding:14px;font-family:inherit;font-size:1rem;border:2px solid #cbd5e1;border-radius:12px;" placeholder="Last Name, Full Name, Role&#10;Doe, John, member&#10;Smith, Jane, leader&#10;Brown, Alex, admin"></textarea>
                <p class="form-hint">Format: Last Name, Full Name, Role (member / leader / admin). One per line. Max 100 members.</p>
            </div>
        `,
        confirmText: 'Import',
        cancelText: 'Cancel',
        onConfirm: handleBulkImport
    });
    modal.open();
}

// ============================================
// Handle Bulk Import
// ============================================
async function handleBulkImport() {
    const raw = document.getElementById('bulkImportArea')?.value?.trim();
    if (!raw) {
        showToast('Please paste member data', 'error');
        return false;
    }

    const lines = raw.split(/\n/).map(l => l.trim()).filter(l => l);
    const members = [];
    const errors = [];

    lines.forEach((line, idx) => {
        const parts = line.split(',').map(p => p.trim());
        if (parts.length < 2) {
            errors.push(`Line ${idx + 1}: needs at least Last Name and Full Name`);
            return;
        }
        members.push({
            lastName: parts[0],
            fullName: parts[1],
            role: parts[2] || 'member'
        });
    });

    if (errors.length > 0) {
        showToast(errors[0], 'error');
        return false;
    }

    const token = getToken();
    showToast('Importing members...', 'info');

    try {
        const data = await bulkImportMembers(token, members);
        if (data.error) {
            showToast('Import failed: ' + data.error, 'error');
            return false;
        }
        showToast(`Imported ${data.added} members. Skipped ${data.skipped.length}.`, 'success');
        renderAdminPage();
        return true;
    } catch (err) {
        showToast('Import failed: ' + err.message, 'error');
        return false;
    }
}

// ============================================
// Master Login Modal
// ============================================
function openMasterLoginModal() {
    const modal = new Modal({
        title: 'Master Login',
        content: `
            <div class="form-group">
                <label>Master Password</label>
                <input type="password" id="masterPasswordInput" placeholder="Enter master password..." style="width:100%;padding:14px;font-size:1rem;border-radius:8px;border:2px solid #cbd5e1;">
                <p id="masterLoginError" style="color:#dc2626;font-size:0.85rem;margin-top:6px;display:none;"></p>
            </div>
        `,
        confirmText: 'Login',
        cancelText: 'Cancel',
        onConfirm: handleMasterLoginSubmit
    });
    modal.open();

    // Enter key support
    document.getElementById('masterPasswordInput')?.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
            handleMasterLoginSubmit();
        }
    });
}

// ============================================
// Handle Master Login Submit
// ============================================
async function handleMasterLoginSubmit() {
    const pwd = document.getElementById('masterPasswordInput')?.value?.trim();
    const errorEl = document.getElementById('masterLoginError');

    if (!pwd) {
        if (errorEl) {
            errorEl.textContent = 'Password required';
            errorEl.style.display = 'block';
        }
        return false;
    }

    if (errorEl) {
        errorEl.style.display = 'none';
    }

    showToast('Verifying master password...', 'info');

    try {
        const data = await masterLogin(pwd);
        if (data.success && data.masterToken) {
            masterToken = data.masterToken;
            setMasterToken(masterToken);
            showToast('Master login successful', 'success');
            renderAdminPage();
            return true;
        } else {
            if (errorEl) {
                errorEl.textContent = data.error || 'Invalid password';
                errorEl.style.display = 'block';
            }
            document.getElementById('masterPasswordInput').value = '';
            document.getElementById('masterPasswordInput').focus();
            return false;
        }
    } catch (err) {
        if (errorEl) {
            errorEl.textContent = 'Login failed: ' + err.message;
            errorEl.style.display = 'block';
        }
        document.getElementById('masterPasswordInput').value = '';
        document.getElementById('masterPasswordInput').focus();
        return false;
    }
}

// ============================================
// Audit Log Modal
// ============================================
async function openAuditLogModal() {
    if (!masterToken) {
        showToast('Master login required', 'error');
        return;
    }

    const modal = new Modal({
        title: 'Audit Log (Last 24h)',
        content: '<div style="padding:16px;color:#6b7280;text-align:center;">Loading audit log...</div>',
        footer: `<button class="btn btn-secondary" id="btnCloseAuditLog">Close</button>`
    });
    modal.open();

    document.getElementById('btnCloseAuditLog')?.addEventListener('click', () => modal.close());

    showToast('Loading audit log...', 'info');

    try {
        const data = await auditLogView(masterToken);
        if (data.error) {
            modal.setContent(`<div class="error-box"><p class="error-text">${escapeHtml(data.error)}</p></div>`);
            return;
        }
        const entries = data.entries || [];
        if (entries.length === 0) {
            modal.setContent(`<div class="error-box"><p class="error-text">No entries in the last 24 hours.</p></div>`);
            return;
        }
        let html = `<div style="max-height:60vh;overflow-y:auto;"><table style="width:100%;font-size:0.85rem;border-collapse:collapse;">`;
        html += `<thead><tr style="background:#f1f5f9;text-align:left;"><th style="padding:8px;">Time</th><th style="padding:8px;">User</th><th style="padding:8px;">Action</th><th style="padding:8px;">Target</th><th style="padding:8px;">Result</th></tr></thead><tbody>`;
        entries.forEach(e => {
            const ts = new Date(e.timestamp).toLocaleString();
            html += `<tr style="border-bottom:1px solid #e2e8f0;"><td style="padding:8px;color:#6b7280;font-size:0.75rem;">${escapeHtml(ts)}</td><td style="padding:8px;">${escapeHtml(e.user)}</td><td style="padding:8px;"><span style="background:#eff6ff;padding:2px 6px;border-radius:4px;font-size:0.75rem;">${escapeHtml(e.action)}</span></td><td style="padding:8px;color:#6b7280;">${escapeHtml(e.target)}</td><td style="padding:8px;font-size:0.75rem;">${escapeHtml(e.result)}</td></tr>`;
        });
        html += `</tbody></table></div>`;
        modal.setContent(html);
    } catch (err) {
        modal.setContent(`<div class="error-box"><p class="error-text">Failed to load audit log.</p></div>`);
    }
}

// ============================================
// Audit Log Toggle
// ============================================
async function handleAuditLogToggle() {
    if (!masterToken) {
        showToast('Master login required', 'error');
        return;
    }

    showToast('Toggling AuditLog visibility...', 'info');

    try {
        const data = await auditLogToggleVisibility(masterToken, auditLogVisibility === 'VERY_HIDDEN' ? 'VISIBLE' : 'VERY_HIDDEN');
        if (data.success) {
            auditLogVisibility = data.visibility;
            showToast('AuditLog is now ' + data.visibility, 'success');
            renderAdminPage();
        } else {
            showToast(data.error || 'Toggle failed', 'error');
        }
    } catch (err) {
        showToast('Toggle failed: ' + err.message, 'error');
    }
}

// ============================================
// Archive Toggle
// ============================================
async function handleArchiveToggle() {
    if (!masterToken) {
        showToast('Master login required', 'error');
        return;
    }

    showToast('Toggling archive visibility...', 'info');

    try {
        const data = await surveyArchiveToggleVisibility(masterToken, archiveVisibility === 'VERY_HIDDEN' ? 'VISIBLE' : 'VERY_HIDDEN');
        if (data.success) {
            archiveVisibility = data.visibility;
            showToast(data.toggled + ' archive sheets are now ' + data.visibility, 'success');
            renderAdminPage();
        } else {
            showToast(data.error || 'Toggle failed', 'error');
        }
    } catch (err) {
        showToast('Toggle failed: ' + err.message, 'error');
    }
}

// ============================================
// Sync Now
// ============================================
async function handleSyncNow() {
    const btn = document.getElementById('btnSyncNow');
    if (!btn) return;

    btn.textContent = 'Syncing...';
    btn.disabled = true;
    showToast('Syncing surveys...', 'info');

    const token = getToken();

    try {
        const data = await syncNow(token);
        const status = document.getElementById('syncStatus');
        if (status) {
            status.style.display = 'block';
            if (data.error) {
                status.textContent = 'Sync failed: ' + data.error;
                status.style.color = '#dc2626';
            } else {
                status.textContent = 'Sync complete: ' + (data.transferred || 0) + ' rows transferred.' + (data.errors ? ' Errors: ' + data.errors : '');
                status.style.color = '#059669';
                showToast('Sync complete!', 'success');
            }
        }
    } catch (err) {
        showToast('Sync failed: ' + err.message, 'error');
    } finally {
        btn.textContent = '🔄 Sync Now';
        btn.disabled = false;
    }
}

// ============================================
// Reset PIN Handlers
// ============================================
async function handleResetPinClick() {
    const input = document.getElementById('resetPinName');
    const targetName = input?.value?.trim();

    if (!targetName) {
        showToast('Select a name from the dropdown.', 'error');
        return;
    }

    const btn = document.getElementById('btnResetPin');
    if (btn) {
        btn.textContent = 'Resetting...';
        btn.disabled = true;
    }

    const token = getToken();

    try {
        const data = await resetPin(token, targetName);
        if (data.success) {
            const msgEl = document.getElementById('resetPinMsg');
            if (msgEl) {
                msgEl.textContent = 'PIN reset for ' + data.fullName + '. They will set a new PIN on next login.';
                msgEl.style.display = 'block';
            }
            if (input) input.value = '';
            showToast('PIN reset successfully!', 'success');
        } else {
            showToast(data.error || 'Reset failed.', 'error');
        }
    } catch (err) {
        showToast('Reset failed. Please try again.', 'error');
    } finally {
        if (btn) {
            btn.textContent = 'Reset PIN';
            btn.disabled = false;
        }
    }
}

// ============================================
// Reset PIN Autocomplete
// ============================================
function setupResetPinAutocomplete() {
    const input = document.getElementById('resetPinName');
    if (!input) return;

    let debounce = null;

    input.addEventListener('input', () => {
        if (debounce) clearTimeout(debounce);
        debounce = setTimeout(async () => {
            const query = input.value.trim();
            const suggestionsBox = document.getElementById('resetPinSuggestions');

            if (!query || query.length < 2) {
                if (suggestionsBox) {
                    suggestionsBox.style.display = 'none';
                    suggestionsBox.innerHTML = '';
                }
                return;
            }

            if (suggestionsBox) {
                suggestionsBox.innerHTML = `<div style="padding:10px 12px;color:#6b7280;font-size:0.9rem;">Searching...</div>`;
                suggestionsBox.style.display = 'block';
            }

            try {
                const data = await searchMembers(query);
                const matches = data.matches || [];

                if (suggestionsBox) {
                    if (matches.length === 0) {
                        suggestionsBox.innerHTML = `<div style="padding:10px 12px;color:#6b7280;font-size:0.9rem;">No matches found</div>`;
                        return;
                    }

suggestionsBox.innerHTML = matches.map(m => {
                    const displayName = m.firstName + " " + m.lastName;
                    return `<div class="reset-pin-suggestion" style="padding:10px 12px;cursor:pointer;border-bottom:1px solid #f3f4f6;font-size:0.95rem;" data-text="${escapeHtml(displayName)}">
                        <span style="font-weight:600;">${escapeHtml(displayName)}</span>
                    </div>`;
                }).join('');

                    document.querySelectorAll('.reset-pin-suggestion').forEach(el => {
                        el.addEventListener('click', () => {
                            input.value = el.dataset.text;
                            if (suggestionsBox) suggestionsBox.style.display = 'none';
                        });
                    });
                }
            } catch (err) {
                if (suggestionsBox) {
                    suggestionsBox.innerHTML = `<div style="padding:10px 12px;color:#dc2626;font-size:0.9rem;">Search failed. Try again.</div>`;
                }
            }
        }, 300);
    });

    document.addEventListener('click', (e) => {
        const box = document.getElementById('resetPinSuggestions');
        if (box && !box.contains(e.target) && e.target !== input) {
            box.style.display = 'none';
        }
    });
}

// ============================================
// Helper Functions
// ============================================
function countSubmissionsForSurvey(surveyName, summaryData) {
    if (!summaryData || !summaryData.dates) return 0;
    let total = 0;
    for (const dateKey of Object.keys(summaryData.dates)) {
        const dayData = summaryData.dates[dateKey];
        if (dayData && dayData[surveyName]) {
            total += dayData[surveyName];
        }
    }
    return total;
}








